import * as React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { Button, IconMenuSecondary, Link } from "atoms";
import { useAuth } from "features/auth";
import { Artist, Project } from "../types";

interface SecondaryMenuProps {
  reviewable: Artist | Project;
  openReportForm: () => void;
}

function SecondaryMenu({ openReportForm, reviewable }: SecondaryMenuProps) {
  const { user } = useAuth();
  const canEdit = user?.roles?.find(
    (r) => r === "qa" || r === "admin" || r === "superAdmin"
  );

  const [isSecondaryMenuOpen, setIsSecondaryMenuOpen] = React.useState(false);
  const toggleSecondaryMenu = () =>
    setIsSecondaryMenuOpen(!isSecondaryMenuOpen);

  const navigate = useNavigate();

  // If user selects menu option, the menu should close after event bubbles up
  const closeSecondaryMenu: React.ReactEventHandler = (event) => {
    if (event.currentTarget !== event.target) {
      setIsSecondaryMenuOpen(false);
    }
  };

  return (
    <SecondaryMenuWrapper>
      <SecondaryMenuToggle
        type="button"
        onClick={toggleSecondaryMenu}
        isOpen={isSecondaryMenuOpen}
      >
        <IconMenuSecondary aria-hidden="true" />
      </SecondaryMenuToggle>

      <SecondaryMenuNav
        isOpen={isSecondaryMenuOpen}
        onClick={closeSecondaryMenu}
      >
        <ul>
          <li>
            <Button
              variant="blank"
              onClick={() => {
                openReportForm();
                navigate("#report-form");
              }}
            >
              Report A Problem
            </Button>
          </li>
          {canEdit && (
            <li>
              <Link
                to={`/${reviewable.type.toLowerCase()}s/${reviewable.id}/edit`}
              >
                Edit {reviewable.type.toLowerCase()}
              </Link>
            </li>
          )}
        </ul>
      </SecondaryMenuNav>
    </SecondaryMenuWrapper>
  );
}

const SecondaryMenuWrapper = styled.div({
  position: "absolute",
  right: 0,
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "flex-end",
  top: 0,
  transform: "translateY(-100%)",
  zIndex: 100,
});

interface WithIsOpen {
  isOpen: boolean;
}

const SecondaryMenuToggle = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<WithIsOpen>(({ isOpen, theme }) => ({
  backgroundColor: isOpen ? theme.colors.gray20 : theme.colors.gray10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  svg: {
    pointerEvents: "none",
  },
  "&::after": {
    display: isOpen ? "block" : "none",
    content: "''",
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
}));

const SecondaryMenuNav = styled.nav<WithIsOpen>(({ theme, isOpen }) => ({
  display: isOpen ? "block" : "none",
  background: theme.colors.bgLight,
  boxShadow: theme.boxShadow.dark,
  borderRadius: theme.borderRadius.md,
  minWidth: "200px",
  width: "max-content",
  maxWidth: "285px",
  padding: "1rem",
  position: "absolute",
  textAlign: "left",
  top: "100%",
  zIndex: 1,
  ul: {
    listStyleType: "none",
    margin: 0,
    padding: 0,
  },
  "li+li": {
    marginTop: ".5rem",
  },
  "ul > li::before": {
    content: '"\\200B"',
    position: "absolute",
    height: 0,
  },
  "a, button": {
    backgroundColor: "transparent",
    padding: 0,
    borderRadius: 0,
    margin: 0,
    textAlign: "left",
    display: "inline-block",
    fontSize: theme.fontSizes.xs,
    fontWeight: "bold",
    textDecoration: "none",
    lineHeight: 1.2,
  },
}));

export { SecondaryMenu };
